import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils"

// Design System 2.0 (2026-10, Phase 1) — consolidated button system: 32px tall, radius 4
// (Tailwind's un-overridden `rounded`). Old variant keys (default/outline/secondary/ghost/
// link/destructive) are KEPT and re-pointed to the new look so the handful of existing
// callers (pagination/calendar/carousel/alert-dialog ui internals, CreativeRadar.jsx) don't
// break — new pages should use primary/secondary/tertiary/destructive/iconOnly instead.
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-3.5 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary: "bg-brand text-ink hover:bg-brand-dim",
        default: "bg-brand text-ink hover:bg-brand-dim",
        secondary: "border border-hairline bg-paper text-ink hover:border-ink",
        outline: "border border-hairline bg-paper text-ink hover:border-ink",
        tertiary: "text-ink hover:bg-surface2",
        ghost: "text-ink hover:bg-surface2",
        link: "text-ink underline-offset-4 hover:underline",
        destructive: "border border-tone-red-dot text-tone-red-text hover:bg-tone-red-bg",
        iconOnly: "text-ink-soft hover:bg-surface2 [&_svg]:size-4",
      },
      size: {
        default: "h-8 px-3.5",
        sm: "h-7 rounded px-2.5 text-[11px]",
        lg: "h-9 px-5",
        icon: "h-8 w-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, loading = false, tooltip, children, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  // Design System 2.0 (Phase 1 FIX PASS): spread `props` FIRST so the computed `disabled`/
  // `aria-label`/`title` below always win — previously `{...props}` was spread LAST and
  // silently re-applied the caller's own `disabled`/`aria-label`, undoing the loading-forced
  // disable (e.g. <Button loading disabled={false}> stayed clickable).
  const accessibleLabel = tooltip || props["aria-label"] || (typeof children === "string" ? children : undefined)
  const content = (
    <Comp
      {...props}
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      disabled={props.disabled || loading}
      aria-busy={loading || undefined}
      title={tooltip}
      aria-label={accessibleLabel}>
      {loading ? (
        <>
          <Loader2 className="animate-spin" aria-hidden="true" />
          <span className="sr-only">{accessibleLabel || "Loading"}</span>
        </>
      ) : children}
    </Comp>
  );
  return content;
})
Button.displayName = "Button"

export { Button, buttonVariants }
